import type { GuardianLink, SubmissionResult, SubmissionStepId } from '@mbs/shared';
import { EXISTING_STUDENT_STEPS, NEW_STUDENT_STEPS, stepPosition } from '@mbs/shared';
import { api, ApiClientError } from './api';
import type { QueueRecord } from './db';

/**
 * The submission sequence, in one place, used both for a live submit and for
 * flushing a queued one.
 *
 * Each finished step is written back into `record.progress`, so a retry resumes
 * from the failure instead of creating a second set of guardians (§7.6) — and a
 * partial success can be reported honestly (§7.7).
 */
export interface SubmissionCallbacks {
  onStep?: (step: SubmissionStepId, position: { number: number; total: number; label: string }) => void;
  onProgress?: (record: QueueRecord) => void | Promise<void>;
}

export async function runSubmission(
  record: QueueRecord,
  callbacks: SubmissionCallbacks = {},
): Promise<SubmissionResult> {
  const steps = record.applicant_type === 'Existing' && record.branch_code !== 'DD'
    ? EXISTING_STUDENT_STEPS
    : NEW_STUDENT_STEPS;

  const announce = (step: SubmissionStepId) => callbacks.onStep?.(step, stepPosition(steps, step));

  // Step 1 — guardians. Skipped when a previous attempt already created them.
  if (!record.progress.guardians?.length) {
    announce('create_guardians');
    const response = await api.post<{ guardians: GuardianLink[] }>(
      '/api/registration/guardians',
      { guardians: record.guardians },
      { correlationId: record.id },
    );
    record.progress.guardians = response.guardians;
    await callbacks.onProgress?.(record);
  }

  const application = {
    ...record.application,
    guardians: record.progress.guardians,
  };

  if (record.applicant_type === 'Existing' && record.branch_code !== 'DD') {
    // Main / MBS #2 existing student: Frappe updates the Student in place and
    // files the applicant for the promoted grade in one call.
    announce('submit_existing');
    const result = await api.post<SubmissionResult>(
      '/api/registration/existing',
      { application, idempotency_key: record.id },
      { correlationId: record.id, idempotencyKey: record.id },
    );
    return result;
  }

  // Step 2 — School ID. Dembi Dollo "Existing" students get a fresh ID exactly
  // like a new one; only `applicant_type` differs (§6.1).
  if (!record.progress.school_id) {
    announce('generate_school_id');
    const response = await api.post<{ school_id: string; student_email_id: string }>(
      '/api/registration/school-id',
      { branch: record.branch_code },
      { correlationId: record.id },
    );
    record.progress.school_id = response.school_id;
    await callbacks.onProgress?.(record);
  }

  // Step 3 — the applicant itself.
  announce('create_applicant');
  return api.post<SubmissionResult>(
    '/api/registration/applicant',
    {
      application: { ...application, custom_school_id: record.progress.school_id },
      idempotency_key: record.id,
    },
    { correlationId: record.id, idempotencyKey: record.id },
  );
}

/**
 * What survived a failed attempt, in words a registrar can act on. Without
 * this, a retry silently duplicates work.
 */
export function describePartialSuccess(record: QueueRecord): string[] {
  const done: string[] = [];
  if (record.progress.guardians?.length) {
    done.push(
      `${record.progress.guardians.length} guardian record(s) were already created or updated (${record.progress.guardians
        .map((g) => g.guardian)
        .join(', ')}). Retrying will not duplicate them.`,
    );
  }
  if (record.progress.school_id) {
    done.push(`School ID ${record.progress.school_id} was already reserved and will be reused.`);
  }
  return done;
}

export function isOffline(error: unknown): boolean {
  return error instanceof ApiClientError && error.offline;
}
